import { DateTime } from 'luxon';
import { z } from 'zod';
import { careAppointmentStatusSchema } from '../api/care-scheduling';
import {
  careAppointmentAttendanceOutcomeSchema,
  careAppointmentAttendanceReasonSchema,
} from '../api/telemedicine';
import { CARE_APPOINTMENT_SOURCE, careAppointmentModalitySchema } from './appointments';
import { careCallTypeSchema } from './care-call-types';

// ─── care appointment history + attribution vocabularies ────────────────────
//
// Governed enums for the attribution columns on `care_appointments` and for
// the append-only `care_appointment_history_entries` table. As with the source
// vocabulary, each `as const` array drives the column's CHECK and every reader
// (ADR-056 Rule 8); the history table is replicated to the warehouse, so these
// values are also the data team's contract for rebuilding the timeline.

/**
 * Who made a change. `member`: the member themselves, in the app or through
 * an agent acting in their session. `staff`: a care person — with their
 * `internal_users.id` when the caller's identity resolved, NULL when a
 * `legacy-compat` caller had no crosswalk row ("not attributable"). `system`:
 * the server on nobody's request — lazy completion, the 1.0 import, the
 * history seed.
 *
 * `healthie_sync` is reserved for the Healthie inbound mirror and has no
 * writer yet. It is declared now so the warehouse contract does not change
 * shape when that writer arrives; it is not dead code.
 */
export const CARE_APPOINTMENT_ACTOR = ['member', 'staff', 'system', 'healthie_sync'] as const;
export const careAppointmentActorSchema = z.enum(CARE_APPOINTMENT_ACTOR);
export type CareAppointmentActor = z.infer<typeof careAppointmentActorSchema>;

/**
 * What a history row records. `snapshot` is the appointment's state at the
 * moment history began for it (the one-time seed, or a 1.0 import), never a
 * claim that it was booked then. The attendance pair splits the first
 * attestation from a later change of outcome or reason.
 */
export const CARE_APPOINTMENT_HISTORY_CHANGE_TYPE = [
  'snapshot',
  'booked',
  'rescheduled',
  'cancelled',
  'completed',
  'attendance_marked',
  'attendance_corrected',
  'modality_changed',
] as const;
export const careAppointmentHistoryChangeTypeSchema = z.enum(CARE_APPOINTMENT_HISTORY_CHANGE_TYPE);
export type CareAppointmentHistoryChangeType = z.infer<
  typeof careAppointmentHistoryChangeTypeSchema
>;

/**
 * The surface a change came through: the booking sources, plus the
 * telemedicine routes (call end, attendance), the booking transaction's own
 * lazy completion, and the one-time seed.
 */
export const CARE_APPOINTMENT_HISTORY_SOURCE = [
  ...CARE_APPOINTMENT_SOURCE,
  'telemedicine',
  'scheduler',
  'backfill',
] as const;
export const careAppointmentHistorySourceSchema = z.enum(CARE_APPOINTMENT_HISTORY_SOURCE);
export type CareAppointmentHistorySource = z.infer<typeof careAppointmentHistorySourceSchema>;

/**
 * The tracked state of an appointment, held in full in `previous_data` /
 * `new_data`: a consumer compares the two to see which fields changed. Instants
 * are ISO-8601 UTC strings. Ids, enum values and instants only — no names, no
 * free text, no member identifier beyond the history row's own `member_id`.
 *
 * Not `.strict()`: rows are read back long after they are written, and a field
 * a later change adds (modality) must not make older rows unreadable.
 */
export const careAppointmentTrackedStateSchema = z.object({
  startsAt: z.string().datetime({ offset: true }),
  durationMinutes: z.number().int().positive(),
  // Null only on a history-only imported row, whose 1.0 practitioner is no
  // Galapagos specialist.
  specialistId: z.string().uuid().nullable(),
  status: careAppointmentStatusSchema,
  callType: careCallTypeSchema.nullable(),
  cancelledAt: z.string().datetime({ offset: true }).nullable(),
  attendanceOutcome: careAppointmentAttendanceOutcomeSchema.nullable(),
  attendanceReason: careAppointmentAttendanceReasonSchema.nullable(),
  modality: careAppointmentModalitySchema.nullable().optional(),
});
export type CareAppointmentTrackedState = z.infer<typeof careAppointmentTrackedStateSchema>;

/** The `care_appointments` columns the tracked state is read from. */
export interface CareAppointmentTrackedColumns {
  startsAt: Date;
  durationMinutes: number;
  specialistId: string | null;
  status: string;
  callType: string | null;
  cancelledAt: Date | null;
  attendanceOutcome: string | null;
  attendanceReason: string | null;
  modality?: string | null;
}

/**
 * Builds the tracked state from a row and parses it, so a value outside a
 * governed vocabulary fails here rather than landing in the warehouse.
 */
export function trackedStateOf(row: CareAppointmentTrackedColumns): CareAppointmentTrackedState {
  return careAppointmentTrackedStateSchema.parse({
    startsAt: row.startsAt.toISOString(),
    durationMinutes: row.durationMinutes,
    specialistId: row.specialistId,
    status: row.status,
    callType: row.callType,
    cancelledAt: row.cancelledAt ? row.cancelledAt.toISOString() : null,
    attendanceOutcome: row.attendanceOutcome,
    attendanceReason: row.attendanceReason,
    modality: row.modality ?? null,
  });
}

/**
 * Whether a cancellation was late under the notice policy copied onto the
 * appointment when it was booked: cancelled less than `noticeMinutes` before
 * the start. Undefined when the row predates the copied policy, so lateness is
 * unknown rather than guessed. Derived, never stored, so a policy change never
 * rewrites history.
 */
export function isLateCancellation(input: {
  startsAt: Date;
  cancelledAt: Date;
  noticeMinutes: number | null;
}): boolean | undefined {
  if (input.noticeMinutes === null) return undefined;
  const deadline = DateTime.fromJSDate(input.startsAt).minus({ minutes: input.noticeMinutes });
  return DateTime.fromJSDate(input.cancelledAt) > deadline;
}
